
import { Component, Prop, Vue } from 'vue-property-decorator';
import { cva } from '@/utils/cva';

// CVA tanımı - Vuetify class'ları kullanarak
const statBoxVariants = cva('pa-4', {
  variants: {
    size: {
      sm: 'pa-3 py-2',
      md: 'pa-4',
      lg: 'pa-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * StatBox - İstatistik gösterimi için component
 * Shadcn UI tarzı, Vuetify 2 ile
 */
@Component
export default class StatBox extends Vue {
  @Prop({ type: String, required: true })
  label!: string;

  @Prop({ required: true })
  value!: string | number;

  @Prop({ type: Number, default: 0 })
  elevation!: number;

  @Prop({ type: String, default: 'grey lighten-5' })
  color!: string;

  @Prop({ type: String, default: 'lg' })
  rounded!: string;

  @Prop({ type: String, default: 'md' })
  size!: 'sm' | 'md' | 'lg';

  @Prop({ type: String })
  format?: 'currency' | 'number' | 'default';

  @Prop({ type: String })
  className?: string;

  @Prop({ type: String })
  icon?: string;

  @Prop({ type: String, default: 'grey darken-1' })
  iconColor!: string;

  @Prop({ type: [Number, String], default: 24 })
  iconSize!: number | string;

  @Prop({ type: String })
  bgColor?: string;

  @Prop({ type: String })
  borderColor?: string;

  get statBoxClasses(): string {
    return statBoxVariants({
      size: this.size,
      className: this.className,
    });
  }

  get colorValue(): string {
    return this.color;
  }

  get bgColorStyle(): Record<string, string> {
    const backgroundColor = this.bgColor || (this.color !== 'grey lighten-5' ? this.color : '#f5f5f5');
    return {
      'background-color': backgroundColor,
      'border': '1px solid rgba(0,0,0,0.08)'
    };
  }

  get combinedStyle(): Record<string, string> {
    const baseStyle = this.bgColorStyle;
    if (this.borderColor) {
      baseStyle['border-left'] = `4px solid ${this.borderColor}`;
      baseStyle['border-top'] = '1px solid rgba(0,0,0,0.08)';
      baseStyle['border-right'] = '1px solid rgba(0,0,0,0.08)';
      baseStyle['border-bottom'] = '1px solid rgba(0,0,0,0.08)';
    }
    return baseStyle;
  }

  get iconContainerStyle(): Record<string, string> {
    // bgColor zaten rgba formatında geliyor, direkt kullan
    if (this.bgColor) {
      return {
        'background-color': this.bgColor
      };
    }
    // Fallback: iconColor'dan rgba oluştur
    if (this.iconColor) {
      // Hex color'ı rgba'ya çevir (basit yaklaşım)
      const hex = this.iconColor.replace('#', '');
      if (hex.length === 6) {
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return {
          'background-color': `rgba(${r}, ${g}, ${b}, 0.1)`
        };
      }
    }
    return {
      'background-color': 'rgba(0, 0, 0, 0.05)'
    };
  }

  get formattedValue(): string {
    if (this.format === 'currency') {
      const numValue = Number(this.value);
      // Tam format: binlik ayırıcı nokta, ondalık virgül (₺187.161,69)
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numValue);
    }
    if (this.format === 'number') {
      return new Intl.NumberFormat('tr-TR').format(Number(this.value));
    }
    return String(this.value);
  }
}
